import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import DataGrid, { Column, Scrolling, SearchPanel } from "devextreme-react/data-grid";
import "devextreme/dist/css/dx.light.css";
import { DateBox } from "devextreme-react/date-box";

import { exportDataGrid as exportDataGridExcel } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridPdf } from "devextreme/pdf_exporter";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";

import { baseUrl } from "../../hooks/useFetch";
import useCobDate from "../../hooks/useCobDate";

type GroupMap = Record<string, string[]>;

const GROUPS: GroupMap = {
  Treasury: ["Lee Evans", "Pemberton"],
  "Market Makers": [
    "Biofuels and Oil",
    "Carbon",
    "France",
    "Power and Gas",
    "Structured Products",
    "Trading Management",
  ],
  "Power and Gas": ["Enrico Antonini", "Shared Power and Gas"],
  "Biofuels and Oil": ["Simon Fothergill", "Ronald / Jelle", "Hugo Cap", "Maxime Florentin", "Biodiesel Margin"],
  "Trading Management": ["James Rosser"],
  "Structured Products": ["Anil Dharan"],
  Carbon: ["Joseph Eagell"],
};

interface IndependentColumn {
  name: string;
  topLevel: boolean;
}

const INDEPENDENT_COLUMNS: IndependentColumn[] = [{ name: "RMS Total", topLevel: true }];
const INDEP_SET = new Set(INDEPENDENT_COLUMNS.map((x) => x.name));
const TOP_LEVEL_INDEPS = INDEPENDENT_COLUMNS.filter((x) => x.topLevel).map((x) => x.name);

const isGroup = (name: string): boolean => Object.prototype.hasOwnProperty.call(GROUPS, name);
const isIndependent = (name: string): boolean => INDEP_SET.has(name);

interface DragScrollXProps {
  children: React.ReactNode;
}

interface DragState {
  down: boolean;
  dragging: boolean;
  startX: number;
  startLeft: number;
  pid: number | null;
}

function DragScrollX({ children }: DragScrollXProps) {
  const ref = useRef<HTMLDivElement>(null);
  const stRef = useRef<DragState>({
    down: false,
    dragging: false,
    startX: 0,
    startLeft: 0,
    pid: null,
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const st = stRef.current;
    const THRESH = 6;

    const isInHeader = (target: EventTarget | null): boolean => {
      if (!target || typeof (target as Element).closest !== "function") return false;
      return !!(target as Element).closest(".dx-datagrid-headers");
    };

    const onDown = (e: PointerEvent) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (isInHeader(e.target)) return;

      st.down = true;
      st.dragging = false;
      st.pid = e.pointerId;
      st.startX = e.clientX;
      st.startLeft = el.scrollLeft;
    };

    const onMove = (e: PointerEvent) => {
      if (!st.down) return;

      const dx = e.clientX - st.startX;

      if (!st.dragging) {
        if (Math.abs(dx) < THRESH) return;

        st.dragging = true;
        try {
          el.setPointerCapture(st.pid!);
        } catch {}
        el.style.cursor = "grabbing";
        el.style.userSelect = "none";
      }

      el.scrollLeft = st.startLeft - dx;
    };

    const onEnd = () => {
      if (!st.down) return;

      st.down = false;

      if (st.dragging) {
        try {
          if (st.pid != null) el.releasePointerCapture(st.pid);
        } catch {}
      }

      st.dragging = false;
      st.pid = null;

      el.style.cursor = "grab";
      el.style.userSelect = "";
    };

    el.addEventListener("pointerdown", onDown, { passive: true });
    el.addEventListener("pointermove", onMove, { passive: true });
    el.addEventListener("pointerup", onEnd, { passive: true });
    el.addEventListener("pointercancel", onEnd, { passive: true });
    el.addEventListener("pointerleave", onEnd, { passive: true });

    el.style.cursor = "grab";

    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onEnd);
      el.removeEventListener("pointercancel", onEnd);
      el.removeEventListener("pointerleave", onEnd);
    };
  }, []);

  return (
    <div
      ref={ref}
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {children}
    </div>
  );
}

export default function MarketRisStressTestsGrid() {
  const [rowData, setRowData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [cobDate, setCobDate, cobLoading] = useCobDate("/marketrisk_stress_tests_latest_cob");
  const [, , , latestCobDate] = useCobDate("/market_risk/sp_matrix_latest_cob");

  const gridRef = useRef<any>(null);

  const [openByGroup, setOpenByGroup] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    Object.keys(GROUPS).forEach((k) => (initial[k] = false));
    return initial;
  });

  const openByGroupRef = useRef(openByGroup);
  useEffect(() => {
    openByGroupRef.current = openByGroup;
  }, [openByGroup]);

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        if (!cobDate) return;
        const formattedSelectedDate = cobDate.toISOString().split("T")[0];
        const res = await fetch(`${baseUrl}/marketrisk_stress_tests?cob_date=${formattedSelectedDate}`, {
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setRowData(json.rows || []);
      } catch (e: any) {
        if (!cancelled) setRowData([]);
        console.error("Failed to load stress grid:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [cobDate]);

  const toggleGroup = useCallback((groupName: string) => {
    const nextOpen = !openByGroupRef.current[groupName];
    setOpenByGroup((prev) => ({ ...prev, [groupName]: nextOpen }));

    const grid = gridRef.current;
    if (!grid) return;

    const children = GROUPS[groupName] || [];
    grid.beginUpdate();
    try {
      children.forEach((child: string) => {
        if (isGroup(child)) {
          // show/hide the subgroup band and its children recursively (by React visibility)
          grid.columnOption(`band:${groupName}::${child}`, "visible", nextOpen);
          // also hide/show the leaves that sit directly under this group
        } else {
          grid.columnOption(child, "visible", nextOpen);
        }
      });
    } finally {
      grid.endUpdate();
    }
  }, []);

  const renderGroup = useCallback(
    (groupName: string, parentVisible: boolean, parentKey: string): React.ReactNode => {
      const open = !!openByGroup[groupName];
      const children = GROUPS[groupName] || [];
      const bandKey = parentKey ? `${parentKey}::${groupName}` : groupName;

      return (
        <Column
          key={`band:${bandKey}`}
          name={`band:${bandKey}`}
          caption={groupName}
          visible={parentVisible}
          headerCellRender={() => (
            <div
              onClick={() => toggleGroup(groupName)}
              style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}
              title="Click to expand/collapse"
            >
              <span style={{ fontSize: 12 }}>{open ? "▾" : "▸"}</span>
              <span>{groupName}</span>
            </div>
          )}
        >
          <Column
            key={`parent:${bandKey}`}
            dataField={groupName}
            caption="Total"
            visible={parentVisible}
            alignment="right"
            width={120}
            format="#,##0"
          />

          {children.map((child, idx) => {
            if (isIndependent(child)) {
              return (
                <Column
                  key={`ind-child:${bandKey}:${child}:${idx}`}
                  dataField={child}
                  caption={child}
                  visible={parentVisible && open}
                  alignment="right"
                  width={120}
                  format="#,##0"
                />
              );
            }

            if (isGroup(child)) {
              return renderGroup(child, parentVisible && open, bandKey);
            }

            return (
              <Column
                key={`leaf:${bandKey}:${child}:${idx}`}
                dataField={child}
                caption={child}
                visible={parentVisible && open}
                alignment="right"
                width={120}
                format="#,##0"
              />
            );
          })}
        </Column>
      );
    },
    [openByGroup, toggleGroup]
  );

  const formatCob = (d: Date | null): string => {
    if (!d) return "no-date";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const onExport = async () => {
    const grid = gridRef.current;
    if (!grid) return;

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Stress");

    await exportDataGridExcel({
      component: grid,
      worksheet: ws as any,
      autoFilterEnabled: true,
      keepColumnWidths: true,
      customizeCell: ({ gridCell, excelCell }: any) => {
        if (!gridCell) return;
        if (gridCell.rowType === "header") excelCell.font = { bold: true };
        if (gridCell.rowType === "data" && typeof gridCell.value === "number") {
          excelCell.numFmt = "#,##0";
        }
      },
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `stress_${formatCob(cobDate)}.xlsx`
    );
  };

  const onExportPdf = async () => {
    const grid = gridRef.current;
    if (!grid) return;

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });

    doc.setFontSize(12);
    doc.text(`Stress Totals (CoB: ${formatCob(cobDate)})`, 40, 28);

    await exportDataGridPdf({
      jsPDFDocument: doc,
      component: grid,
      topLeft: { x: 20, y: 50 },
      customizeCell: ({ pdfCell }: any) => {
        if (typeof pdfCell.value === "number") {
          pdfCell.horizontalAlign = "right";
        }
      },
    });

    doc.save(`stress_${formatCob(cobDate)}.pdf`);
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: "auto 1fr",
        gap: 12,
        marginTop: 12,
        alignItems: "start",
        minHeight: 0,
      }}
    >
      <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", margin: 0, padding: 0 }}>
        <DateBox  inputAttr={{ style: "font-size:12px" }}
          displayFormat="dd/MM/yyyy"
          useMaskBehavior
          pickerType="calendar"
          value={cobDate}
          onValueChanged={(e: any) => setCobDate(e.value)}
          width={150}
          height={30}
          max={latestCobDate ?? undefined}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateRows: "1fr auto",
          gap: 8,
           marginTop: -25,

          overflow: "visible",
          background: "transparent",
          minHeight: 0,
        }}
      >
        <DragScrollX>
           <div >
            <DataGrid
              className="compact-grid"
              dataSource={rowData}
              showBorders
              width="auto"
              wordWrapEnabled
              columnAutoWidth={false}
              repaintChangesOnly={true}
              renderAsync={true}
              onInitialized={(e: any) => {
                gridRef.current = e.component;
              }}
               onCellPrepared={(e: any) => {
    if (e.rowType !== "data") return;

    const v = e.value;
    if (typeof v === "number" && v < 0) {
      e.cellElement.style.color = "#d9534f";   // red
      e.cellElement.style.fontWeight = "600";  // optional
    }
  }}
            >
              <SearchPanel
              visible={true}
              width={150}

              placeholder="Search stress category / name…"
              highlightSearchText={true}
              highlightCaseSensitive={false}
              />
              <Scrolling mode="standard" columnRenderingMode="virtual" />

              <Column dataField="STRESS_CATEGORY" caption="Stress Category" width={160} />
              <Column dataField="STRESS_NAME" caption="Stress Name" width={260} />
              <Column dataField="START_DATE" caption="Start Date" width={120} />
              <Column dataField="END_DATE" caption="End Date" width={120} />

              {TOP_LEVEL_INDEPS.map((c) => (
                <Column key={`ind-top:${c}`} dataField={c} caption={c} alignment="right" width={120} format="#,##0" />
              ))}

              {renderGroup("Treasury", true, "")}
              {renderGroup("Market Makers", true, "")}
            </DataGrid>
          </div>
        </DragScrollX>

        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 20 }}>
          <button
            onClick={onExport}
            style={{
              padding: "10px 18px",
              borderRadius: 4,
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
          >
            Export to Excel
          </button>

          <button
            onClick={onExportPdf}
            style={{
              padding: "10px 18px",
              borderRadius: 4,
              backgroundColor: "#d9534f",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
          >
            Export to PDF
          </button>
        </div>
      </div>
    </div>
  );
}
